import { QueryFields } from "@directus/sdk";
import { fetchCityEscapeById, fetchCityEscapes } from "../api/city_escapes";
import { fetchProps } from "../api/props";
import { GaripalliDirectusClient } from "../directus/client";
import {
  CityEscape,
  Props,
  Scene,
  StepChallengeItem,
} from "../directus/collections";
import { Schema } from "../directus/schema";
import { GaripalliIDB } from "../extensions/idb";
import { isStepChallengeItem } from "../libs/gameAnalysis/enigma";
import {
  ChallengeDetails,
  CityEscapeData,
  SceneData,
} from "../types/cityEscape";
import { findChallengeOrigins, findChallengeTargets } from "./enigma";

const CITY_ESCAPE_FLOW_FIELDS = [
  "id",
  "translations.name",
  "props.*",
  "props.translations.*",
  "props.partners.*",
  "props.media.*",
  "scenes.id",
  "scenes.city_escape",
  "scenes.translations.*",
  "scenes.output_scenes_exits.*",
  "scenes.output_scenes_exits.translations.*",
  "scenes.steps.*",
  "scenes.steps.item.translations.*",
  "scenes.steps.item.translations.media.*",
  "scenes.steps.item.*",
  "scenes.steps.item.possible_correct_answers.*",
  "scenes.steps.item.managed_answers.*",
  "scenes.steps.item.managed_answers.answers.*",
  "scenes.steps.item.managed_answers.translations.*",
  "scenes.steps.item.character.*",
  "scenes.steps.item.character.translations.*",
  "scenes.steps.item.character.avatar.*",
  "scenes.steps.item.indepth_analysis.*",
  "scenes.steps.item.indepth_analysis.translations.*",
  "scenes.is_start",
] as QueryFields<Schema, CityEscape>;

export function toCityEscapeData(cityEscape: CityEscape): CityEscapeData {
  return {
    id: cityEscape.id,
    label: cityEscape.translations[0].name,
    scenes: cityEscape.scenes.map((scene) => toSceneData(cityEscape, scene)),
    data: cityEscape,
  };
}

export function isAlternativePath(allScenes: Scene[], scene: Scene): boolean {
  const iterator = scenesIterator(allScenes);
  iterator.setCursor(scene.id);
  const prevScenes = iterator.prev();
  return prevScenes.every((s) =>
    s.output_scenes_exits.some((e) => e.alternative_scene === scene.id)
  );
}

export function toSceneData(cityEscape: CityEscape, scene: Scene): SceneData {
  return {
    id: scene.id,
    label: scene.translations[0].name,
    isStart: scene.is_start,
    isAlternative: isAlternativePath(cityEscape.scenes, scene),
    challenges: scene.steps.reduce((acc, step) => {
      if (!isStepChallengeItem(step)) return acc;
      return [...acc, toChallengeDetails(cityEscape, scene, step)];
    }, [] as ChallengeDetails[]),
    data: scene,
  };
}

export function toChallengeDetails(
  cityEscape: CityEscape,
  scene: Scene,
  challenge: StepChallengeItem
): ChallengeDetails {
  const indexInScene =
    scene.steps
      .filter((s) => isStepChallengeItem(s))
      .sort((a, b) => a.sort - b.sort)
      .findIndex((c) => c.id === challenge.id) + 1;

  if (!indexInScene) {
    throw new Error(
      `Challenge with id ${challenge.id} not found in scene ${scene.id}`
    );
  }

  return {
    id: challenge.item.id,
    label: `${scene.translations[0].name} - ${indexInScene}`,
    textContent: challenge.item.translations[0]?.text || "media",
    cityEscape: {
      id: cityEscape.id,
      title: cityEscape.translations[0].name,
    },
    scene: {
      id: scene.id,
      name: scene.translations[0].name,
      isStart: scene.is_start,
      isAlternative: isAlternativePath(cityEscape.scenes, scene),
    },
    meta: {
      indexInScene,
      next: findChallengeTargets(cityEscape.scenes, scene, challenge),
      prev: findChallengeOrigins(cityEscape.scenes, scene, challenge),
    },
    data: challenge.item,
  };
}

export async function getCityEscapeById(
  client: GaripalliDirectusClient,
  cityEscapeId: number | string
): Promise<CityEscape> {
  const cityEscape = await fetchCityEscapeById(client, String(cityEscapeId), {
    fields: CITY_ESCAPE_FLOW_FIELDS,
  });
  return cityEscape;
}

export async function getCityEscapeProps(
  client: GaripalliDirectusClient,
  cityEscapeId: number | string
): Promise<Props[]> {
  const cityEscapeProps = await fetchProps(client, {
    filter: { city_escapes: {} },
    fields: [
      "*",
      "translations.*",
      "partners.*",
      "partners.translations.*",
      "media.*",
    ] as QueryFields<Schema, Props>,
  });
  return cityEscapeProps;
}

export async function fetchCityEscapeDatasource(
  client: GaripalliDirectusClient
): Promise<CityEscapeData[]> {
  const cityEscapes = await fetchCityEscapes(client, {
    fields: CITY_ESCAPE_FLOW_FIELDS,
    sort: ["translations.name"] as any,
    limit: 1000,
  });

  return cityEscapes.map((cityEscape) => toCityEscapeData(cityEscape));
}

export async function syncCityEscapes(
  client: GaripalliDirectusClient,
  db: GaripalliIDB
): Promise<CityEscapeData[]> {
  console.log("sync city escapes");
  const cityEscapeData = await fetchCityEscapeDatasource(client);
  const city_escapes = db.objectStore("city_escapes");
  await city_escapes.clear();
  await city_escapes.bulkAdd(cityEscapeData);
  return cityEscapeData;
}

export async function getCityEscapeDatasource(
  client: GaripalliDirectusClient,
  db: GaripalliIDB
) {
  return await db.getAll("city_escapes");
}

export function scenesIterator(scenes: Scene[], startSceneId?: string) {
  let currents: Scene[] = [];

  const idToSceneMap = scenes.reduce((acc, scene) => {
    acc[scene.id] = scene;
    return acc;
  }, {} as Record<string, Scene>);

  const next = () => {
    // console.debug("Finding next scenes from currents:", currents);
    const nexts = currents
      .map((scene) => {
        if (!scene.output_scenes_exits) return [];

        return scene.output_scenes_exits
          .map((exit) => {
            const output = [idToSceneMap[exit.target_scene]];
            if (exit.alternative_scene) {
              output.push(idToSceneMap[exit.alternative_scene]);
            }
            return output;
          })
          .flat();
      })
      .flat();

    if (
      currents.some((s) => s.output_scenes_exits.length > 0) &&
      nexts.length === 0
    ) {
      console.error("Current scenes have exits but no next scenes found:", {
        nexts,
        currents,
      });
      throw new Error(`Cannot find scenes with ids ${nexts}`);
    }

    currents = nexts as Scene[];

    return nexts;
  };

  const prev = () => {
    const prevs = scenes.filter((s) =>
      s.output_scenes_exits.some((exit) => {
        const result = [...currents].some((scene) => {
          return (
            scene.id &&
            [exit.target_scene, exit.alternative_scene].includes(scene.id)
          );
        });
        return result;
      })
    );

    currents = prevs;
    return prevs;
  };

  const findNextScenesWithChallenge = (sceneId: string): PathNode[] => {
    const targetScene = idToSceneMap[sceneId];
    if (!targetScene) return [];
    else if (!targetScene.output_scenes_exits) return [];

    const nexts = targetScene.output_scenes_exits.reduce((acc, exit) => {
      const target = idToSceneMap[exit.target_scene];
      const alternative = idToSceneMap[exit.alternative_scene ?? ""];

      if (target) {
        acc.push({ scene: target, isAlternatePath: false });
      }
      if (alternative) {
        acc.push({ scene: alternative, isAlternatePath: true });
      }
      return acc;
    }, [] as PathNode[]);

    return nexts
      .map((s) =>
        s.scene.steps.some((step) => step.collection === "steps_challenge")
          ? s
          : findNextScenesWithChallenge(s.scene.id)
      )
      .flat();
  };

  type PathNode = {
    scene: Scene;
    isAlternatePath: boolean;
  };

  const findPrevScenesWithChallenge = (sceneId: string): PathNode[] => {
    const targetScene = idToSceneMap[sceneId];
    if (!targetScene) return [];
    else if (!targetScene.output_scenes_exits) return [];

    const prevs = scenes.reduce((acc, s) => {
      if (
        s.output_scenes_exits.some((e) => e.target_scene === targetScene.id)
      ) {
        acc.push({ scene: s, isAlternatePath: false });
      } else if (
        s.output_scenes_exits.some(
          (exit) => exit.alternative_scene === targetScene.id
        )
      ) {
        acc.push({ scene: s, isAlternatePath: true });
      }
      return acc;
    }, [] as PathNode[]);

    return prevs
      .map((s) =>
        s.scene.steps.some((step) => step.collection === "steps_challenge")
          ? s
          : findPrevScenesWithChallenge(s.scene.id)
      )
      .flat();
  };

  const setCursor = (sceneId: string) => {
    const targetScene = scenes.find((s) => s.id === sceneId);
    if (targetScene) {
      currents = [targetScene];
    } else {
      throw new Error(`Cannot find scene with id ${sceneId}`);
    }
  };

  let _startingScenes: Scene[] | null = null;
  const restart = () => {
    if (_startingScenes) {
      return _startingScenes;
    }

    currents = startSceneId
      ? ([scenes.find((s) => s.id === startSceneId)].filter(Boolean) as Scene[])
      : [
          scenes.find((s) => s.is_start) ||
            scenes.filter((s) => !scenes.some((s) => s.is_start)),
        ].flat();
    _startingScenes = currents;
  };

  restart();

  return {
    next,
    prev,
    findNextScenesWithChallenge,
    findPrevScenesWithChallenge,
    setCursor,
    restart,
  };
}
